import React, { useState, useEffect } from 'react';
import { ORDER_STATUS } from '../orderStatus';
import { api } from '../api';
import { BRAND_NAME } from '../brand';
import Modal from './Modal';
import OrderModal from './OrderModal';
import { CertificateDetailModal } from '../pages/CertificatesPage';

/**
 * Модалка профиля клиента — самодостаточный компонент:
 *   <ClientProfileModal clientId={42} onClose={...} showToast={...} onClientChange={...} />
 *
 * Грузит клиента и его историю по clientId. onClientChange вызывается
 * когда баланс/состав аккаунтов изменился (родитель может обновить свой список).
 */
function ClientProfileModal({ clientId, onClose, showToast, onClientChange }) {
  const [selectedClient, setSelectedClient] = useState(null);
  const [loadingClient, setLoadingClient] = useState(true);

  // Форма начисления/списания бонусов
  const [bonusForm, setBonusForm] = useState({ amount: '', comment: '' });
  const [bonusAction, setBonusAction] = useState(null);

  // История бонусов / заказов
  const [clientOrders, setClientOrders] = useState([]);
  const [ordersOffset, setOrdersOffset] = useState(0);
  const [hasMoreOrders, setHasMoreOrders] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Объединение / разъединение аккаунтов
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSearchInput, setMergeSearchInput] = useState('');
  const [mergeSearchResults, setMergeSearchResults] = useState([]);
  const [mergeSearching, setMergeSearching] = useState(false);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [merging, setMerging] = useState(false);
  const [unmergeConfirm, setUnmergeConfirm] = useState(null);
  const [unmergePreview, setUnmergePreview] = useState(null);
  const [unmergeBonusRecipient, setUnmergeBonusRecipient] = useState('main');

  // Просмотр заказа / сертификата из истории
  const [selectedOrderView, setSelectedOrderView] = useState(null);
  const [selectedCertView, setSelectedCertView] = useState(null);

  // Загружаем клиента и историю при монтировании / смене clientId
  useEffect(() => {
    let cancelled = false;
    async function loadClient() {
      setLoadingClient(true);
      try {
        const data = await api.getUserById(clientId);
        if (!cancelled) {
          setSelectedClient(data);
        }
      } catch (e) {
        if (!cancelled) showToast('Ошибка загрузки клиента', 'error');
      } finally {
        if (!cancelled) setLoadingClient(false);
      }
    }
    loadClient();
    loadClientOrders(clientId, 0);
    return () => { cancelled = true; };
  }, [clientId]);

  const loadClientOrders = async (userId, offset) => {
    setLoadingOrders(true);
    try {
      const data = await api.getUserOrders(userId, 10, offset);
      if (offset === 0) {
        setClientOrders(data.orders || []);
      } else {
        setClientOrders(prev => [...prev, ...(data.orders || [])]);
      }
      setOrdersOffset(offset + 10);
      setHasMoreOrders(data.has_more || false);
    } catch (error) {
      showToast('Ошибка загрузки истории', 'error');
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleLoadMoreOrders = () => {
    if (selectedClient) loadClientOrders(selectedClient.id, ordersOffset);
  };

  // Перезагрузка клиента + истории после закрытия вложенных карточек
  // (заказ/сертификат): там могли поменять статус или баланс.
  const refreshClientAndHistory = async () => {
    loadClientOrders(clientId, 0);
    try {
      const data = await api.getUserById(clientId);
      setSelectedClient(data);
      onClientChange && onClientChange({ id: data.id, bonus_balance: data.bonus_balance });
    } catch (e) { /* история уже перезагружается, ошибку профиля глотаем */ }
  };

  const openOrderView = async (orderId) => {
    try {
      const order = await api.getOrderById(orderId);
      setSelectedOrderView(order);
    } catch (error) {
      showToast('Ошибка загрузки заказа', 'error');
    }
  };

  const handleBonusAction = async (action) => {
    if (!bonusForm.amount || parseFloat(bonusForm.amount) <= 0) {
      showToast('Введите корректную сумму', 'error');
      return;
    }
    if (!bonusForm.comment.trim()) {
      showToast('Укажите комментарий', 'error');
      return;
    }
    try {
      const amount = parseFloat(bonusForm.amount);
      const comment = bonusForm.comment.trim();
      const cid = selectedClient.id;
      let updatedUser;
      if (action === 'add') {
        updatedUser = await api.addUserBonus(cid, amount, comment);
        showToast(`Счёт ${BRAND_NAME} пополнен`);
      } else if (action === 'subtract') {
        updatedUser = await api.subtractUserBonus(cid, amount, comment);
        showToast(`Списано со счёта ${BRAND_NAME}`);
      }
      setSelectedClient(prev => ({ ...prev, bonus_balance: updatedUser.bonus_balance }));
      onClientChange && onClientChange({ id: cid, bonus_balance: updatedUser.bonus_balance });
      loadClientOrders(cid, 0);
      setBonusForm({ amount: '', comment: '' });
      setBonusAction(null);
    } catch (error) {
      showToast(error.message || 'Ошибка операции', 'error');
    }
  };

  const handleMergeSearch = async () => {
    const q = mergeSearchInput.trim();
    if (!q) return;
    setMergeSearching(true);
    try {
      const data = await api.getUsers({ searchTelegramId: q, limit: 10, offset: 0 });
      const filtered = (data.users || []).filter(u => u.id !== selectedClient.id);
      setMergeSearchResults(filtered);
    } catch (e) {
      showToast('Ошибка поиска', 'error');
    } finally {
      setMergeSearching(false);
    }
  };

  const handleMerge = async () => {
    if (!mergeTarget) return;
    setMerging(true);
    try {
      const result = await api.mergeUsers(mergeTarget.id, selectedClient.id);
      showToast(result.message || 'Аккаунты объединены');
      if (result.user) {
        setSelectedClient(result.user);
        onClientChange && onClientChange(result.user);
      }
      setMergeMode(false);
      setMergeTarget(null);
      setMergeSearchResults([]);
      setMergeSearchInput('');
      if (result.user?.id) loadClientOrders(result.user.id, 0);
    } catch (e) {
      showToast(e.message || 'Ошибка объединения', 'error');
    } finally {
      setMerging(false);
    }
  };

  const handlePickUnmergePlatform = async (platform) => {
    setUnmergeConfirm(platform);
    setUnmergeBonusRecipient('main');
    setUnmergePreview(null);
    try {
      const data = await api.getUnmergePreview(selectedClient.id, platform);
      setUnmergePreview(data);
    } catch (e) {
      showToast('Не удалось загрузить превью', 'error');
    }
  };

  const handleUnmerge = async (platform) => {
    try {
      const result = await api.unmergeUser(selectedClient.id, platform, unmergeBonusRecipient);
      showToast(result.message || 'Аккаунт разъединён');
      if (result.user) {
        setSelectedClient(result.user);
        onClientChange && onClientChange(result.user);
      }
      setUnmergeConfirm(null);
      setUnmergePreview(null);
      setUnmergeBonusRecipient('main');
    } catch (e) {
      showToast(e.message || 'Ошибка разъединения', 'error');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatPhone = (phone) => {
    if (!phone) return null;
    const d = String(phone).replace(/\D/g, '').slice(-10);
    if (d.length !== 10) return phone;
    return `+7 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 8)}-${d.slice(8, 10)}`;
  };

  const statusLabels = ORDER_STATUS; // единые подписи (src/orderStatus.js)

  if (loadingClient || !selectedClient) {
    return (
      <Modal title="Профиль клиента" size="xl" onClose={onClose}
        footer={<button className="btn btn-secondary" onClick={onClose}>Закрыть</button>}
      >
        <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Загрузка...</div>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        title="Профиль клиента"
        size="xl"
        onClose={onClose}
        footer={<button className="btn btn-secondary" onClick={onClose}>Закрыть</button>}
      >
        {/* Информация о клиенте */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '12px' }}>

            {selectedClient.telegram_id && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: '0 0 auto' }}>
                <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: '#E1F5FE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', color: '#0288D1' }}>👤</div>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '2px' }}>
                    {[selectedClient.tg_first_name, selectedClient.tg_last_name].filter(Boolean).join(' ') || 'Telegram аккаунт'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {selectedClient.tg_username ? `@${selectedClient.tg_username}` : ''}
                  </div>
                  <div style={{ fontSize: '12px', color: '#999', fontFamily: 'monospace' }}>
                    Telegram ID: {selectedClient.telegram_id}
                  </div>
                </div>
              </div>
            )}

            {selectedClient.telegram_id && selectedClient.max_id && (
              <div style={{ width: '1px', background: '#eee', alignSelf: 'stretch' }} />
            )}

            {selectedClient.max_id && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: '0 0 auto' }}>
                <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: '#DEEFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', color: '#26689A' }}>👤</div>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '2px' }}>
                    {[selectedClient.max_first_name, selectedClient.max_last_name].filter(Boolean).join(' ') || 'Max аккаунт'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {selectedClient.max_username ? `@${selectedClient.max_username}` : ''}
                  </div>
                  <div style={{ fontSize: '12px', color: '#999', fontFamily: 'monospace' }}>
                    Max ID: {selectedClient.max_id}
                  </div>
                </div>
              </div>
            )}

            {selectedClient.phone && (selectedClient.telegram_id || selectedClient.max_id) && (
              <div style={{ width: '1px', background: '#eee', alignSelf: 'stretch' }} />
            )}

            {selectedClient.phone && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: '0 0 auto' }}>
                <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: '#DFF2E8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', color: 'var(--primary-color)' }}>👤</div>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '2px' }}>
                    {selectedClient.web_first_name || 'Web аккаунт'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>WEB</div>
                  <div style={{ fontSize: '12px', color: '#999', fontFamily: 'monospace' }}>
                    {formatPhone(selectedClient.phone) || selectedClient.phone}
                  </div>
                </div>
              </div>
            )}

            {!selectedClient.telegram_id && !selectedClient.max_id && !selectedClient.phone && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: '0 0 auto' }}>
                <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: '#EBEDEC', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', color: '#999' }}>👤</div>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 700 }}>{selectedClient.name || 'Без имени'}</div>
                </div>
              </div>
            )}

            <div style={{ width: '1px', background: '#eee', alignSelf: 'stretch' }} />

            {/* Баланс + кнопки */}
            <div style={{ flex: '0 0 auto', textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#999', marginBottom: '2px' }}>Баланс счёта {BRAND_NAME}</div>
              <div style={{ fontSize: '26px', fontWeight: 700, color: 'var(--primary-color)', marginBottom: '10px' }}>
                {selectedClient.bonus_balance} ₽
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <button className="btn btn-primary" onClick={() => setBonusAction(bonusAction === 'add' ? null : 'add')} style={{ fontSize: '13px', padding: '6px 14px' }}>Начислить</button>
                <button className="btn btn-secondary" onClick={() => setBonusAction(bonusAction === 'subtract' ? null : 'subtract')} style={{ fontSize: '13px', padding: '6px 14px' }}>Списать</button>
              </div>
            </div>

            {bonusAction && <div style={{ width: '1px', background: '#eee', alignSelf: 'stretch' }} />}

            {bonusAction && (
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px', color: bonusAction === 'add' ? '#359250' : '#CB4D45' }}>
                  {bonusAction === 'add' ? `Пополнение счёта ${BRAND_NAME}` : `Списание со счёта ${BRAND_NAME}`}
                </div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input type="number" className="form-input" placeholder="Сумма" value={bonusForm.amount}
                    onChange={(e) => setBonusForm(prev => ({ ...prev, amount: e.target.value }))}
                    min="0" style={{ flex: '0 0 120px' }} />
                  <input type="text" className="form-input" placeholder="Комментарий" value={bonusForm.comment}
                    onChange={(e) => setBonusForm(prev => ({ ...prev, comment: e.target.value }))}
                    style={{ flex: 1 }} />
                </div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" onClick={() => { setBonusAction(null); setBonusForm({ amount: '', comment: '' }); }} style={{ fontSize: '13px', padding: '6px 12px' }}>Отмена</button>
                  <button className="btn btn-primary" onClick={() => handleBonusAction(bonusAction)} style={{ fontSize: '13px', padding: '6px 12px' }}>Подтвердить</button>
                </div>
              </div>
            )}
          </div>
          <div style={{ fontSize: '13px', color: '#666' }}>
            Дата регистрации: {formatDate(selectedClient.created_at)}
          </div>
          {selectedClient.first_source && (
            <div style={{ fontSize: '13px', color: '#666' }}>
              Откуда пришёл: <b style={{ color: '#1C3D2E' }}>{selectedClient.first_source}</b>
              {selectedClient.first_touch_at ? ` · ${formatDate(selectedClient.first_touch_at)}` : ''}
            </div>
          )}
        </div>

        {/* Объединение аккаунтов */}
        {(() => {
          const platformCount = (selectedClient.telegram_id ? 1 : 0) + (selectedClient.max_id ? 1 : 0) + (selectedClient.phone ? 1 : 0);
          const isMerged = platformCount >= 2;
          return (
            <div style={{ marginBottom: '24px', padding: '16px', background: '#F9F9F6', '--ch': '8px'}}>
              <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: '#333' }}>Объединение аккаунтов</div>
              {isMerged ? (
                <div>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                    {[
                      selectedClient.telegram_id && <span key="tg">Telegram ID: <b>{selectedClient.telegram_id}</b></span>,
                      selectedClient.max_id && <span key="mx">Max ID: <b>{selectedClient.max_id}</b></span>,
                      selectedClient.phone && <span key="wb">Web: <b>{formatPhone(selectedClient.phone) || selectedClient.phone}</b></span>,
                    ].filter(Boolean).reduce((acc, el, i) => acc.length === 0 ? [el] : [...acc, <span key={`s${i}`}> + </span>, el], [])}
                    {selectedClient.merged_at && <span style={{ color: '#999' }}> (объединён {formatDate(selectedClient.merged_at)})</span>}
                  </div>
                  {!unmergeConfirm ? (
                    <button className="btn btn-secondary" onClick={() => setUnmergeConfirm('choose')} style={{ fontSize: '13px', padding: '6px 14px' }}>Разъединить</button>
                  ) : unmergeConfirm === 'choose' ? (
                    <div>
                      <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>Какой аккаунт отделить?</div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {selectedClient.telegram_id && <button className="btn btn-secondary" onClick={() => handlePickUnmergePlatform('telegram')} style={{ fontSize: '13px', padding: '6px 12px' }}>Отделить Telegram ({selectedClient.telegram_id})</button>}
                        {selectedClient.max_id && <button className="btn btn-secondary" onClick={() => handlePickUnmergePlatform('max')} style={{ fontSize: '13px', padding: '6px 12px' }}>Отделить Max ({selectedClient.max_id})</button>}
                        {selectedClient.phone && <button className="btn btn-secondary" onClick={() => handlePickUnmergePlatform('web')} style={{ fontSize: '13px', padding: '6px 12px' }}>Отделить Web ({formatPhone(selectedClient.phone) || selectedClient.phone})</button>}
                        <button className="btn btn-secondary" onClick={() => { setUnmergeConfirm(null); setUnmergePreview(null); }} style={{ fontSize: '13px', padding: '6px 12px', color: '#999' }}>Отмена</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {(() => {
                        const platformLabel = unmergeConfirm === 'telegram' ? 'Telegram' : unmergeConfirm === 'max' ? 'Max' : 'Web';
                        const splitId = unmergeConfirm === 'telegram' ? selectedClient.telegram_id
                          : unmergeConfirm === 'max' ? selectedClient.max_id
                          : (formatPhone(selectedClient.phone) || selectedClient.phone);
                        return (
                          <>
                            <div style={{ fontSize: '13px', color: '#CB4D45', marginBottom: '10px' }}>
                              Отделить <b>{platformLabel}</b>: <b>{splitId}</b>?
                            </div>
                            {unmergePreview ? (
                              <div style={{ fontSize: '13px', color: '#333', marginBottom: '10px', background: '#fff', padding: '10px 12px', '--ch': '6px', '--stroke': '#D8DDDA', border: '1px solid #D8DDDA' }}>
                                <div style={{ marginBottom: 4 }}>
                                  Заказы платформы <b>{platformLabel}</b>: <b>{unmergePreview.orders_count}</b>
                                  {unmergePreview.orders_total_sum > 0 && <span style={{ color: '#666' }}> ({unmergePreview.orders_total_sum} ₽)</span>}
                                  {' '}— уйдут к отделяемому
                                </div>
                                <div style={{ marginBottom: 8 }}>Счёт {BRAND_NAME} клиента: <b>{unmergePreview.bonus_balance} ₽</b></div>
                                {unmergePreview.bonus_balance > 0 && (
                                  <div style={{ marginBottom: 4 }}>
                                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Куда передать счёт {BRAND_NAME}:</div>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, fontSize: 13, cursor: 'pointer' }}>
                                      <input type="radio" name="bonus_recipient" value="main" checked={unmergeBonusRecipient === 'main'} onChange={() => setUnmergeBonusRecipient('main')} />
                                      Оставить на основном аккаунте
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                                      <input type="radio" name="bonus_recipient" value="split" checked={unmergeBonusRecipient === 'split'} onChange={() => setUnmergeBonusRecipient('split')} />
                                      Передать отделяемому ({platformLabel})
                                    </label>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>Загрузка превью...</div>
                            )}
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button className="btn btn-primary" onClick={() => handleUnmerge(unmergeConfirm)} style={{ fontSize: '13px', padding: '6px 14px', background: '#CB4D45' }}>Подтвердить разъединение</button>
                              <button className="btn btn-secondary" onClick={() => { setUnmergeConfirm('choose'); setUnmergePreview(null); }} style={{ fontSize: '13px', padding: '6px 12px' }}>Назад</button>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  {!mergeMode ? (
                    <button className="btn btn-primary" onClick={() => setMergeMode(true)} style={{ fontSize: '13px', padding: '6px 14px' }}>Объединить с другим аккаунтом</button>
                  ) : (
                    <div>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <input type="text" className="form-input" placeholder="Telegram ID или Max ID" value={mergeSearchInput}
                          onChange={(e) => setMergeSearchInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleMergeSearch()} style={{ flex: 1 }} />
                        <button className="btn btn-primary" onClick={handleMergeSearch} disabled={mergeSearching} style={{ fontSize: '13px' }}>
                          {mergeSearching ? '...' : 'Найти'}
                        </button>
                        <button className="btn btn-secondary" onClick={() => { setMergeMode(false); setMergeSearchResults([]); setMergeTarget(null); setMergeSearchInput(''); }} style={{ fontSize: '13px' }}>Отмена</button>
                      </div>
                      {mergeSearchResults.length > 0 && !mergeTarget && (
                        <div style={{ maxHeight: '150px', overflow: 'auto', border: '1px solid #eee', '--ch': '6px', '--stroke': '#eee'}}>
                          {mergeSearchResults.map(u => (
                            <div key={u.id} onClick={() => setMergeTarget(u)} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #F4F4EF', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#F0F8FF'}
                              onMouseLeave={e => e.currentTarget.style.background = ''}>
                              <span style={{ fontSize: '13px' }}>{u.name || 'Без имени'} {u.username ? `(@${u.username})` : ''}</span>
                              <span style={{ fontSize: '12px', color: '#999', fontFamily: 'monospace' }}>
                                {u.telegram_id ? `TG: ${u.telegram_id}` : ''} {u.max_id ? `Max: ${u.max_id}` : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {mergeSearchResults.length === 0 && mergeSearchInput && !mergeSearching && (
                        <div style={{ fontSize: '13px', color: '#999', padding: '8px' }}>Не найдено</div>
                      )}
                      {mergeTarget && (
                        <div style={{ padding: '12px', background: '#fff', '--ch': '6px', '--stroke': '#D8DDDA', border: '1px solid #D8DDDA' }}>
                          <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                            <b>{selectedClient.name}</b> ({selectedClient.telegram_id || selectedClient.max_id})
                            {' ← '}
                            <b>{mergeTarget.name}</b> ({mergeTarget.telegram_id || mergeTarget.max_id})
                          </div>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                            Счета {BRAND_NAME} будут суммированы. Заказы и подписки перенесутся. Второй аккаунт будет удалён.
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button className="btn btn-primary" onClick={handleMerge} disabled={merging} style={{ fontSize: '13px', padding: '6px 14px' }}>
                              {merging ? 'Объединяю...' : 'Объединить'}
                            </button>
                            <button className="btn btn-secondary" onClick={() => setMergeTarget(null)} style={{ fontSize: '13px', padding: '6px 12px' }}>Отмена</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* История бонусов */}
        <div>
          <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '12px', color: '#333' }}>История счёта {BRAND_NAME}</div>
          {clientOrders.length === 0 && !loadingOrders ? (
            <div style={{ textAlign: 'center', color: '#999', padding: '16px', background: '#F9F9F6', '--ch': '6px'}}>История пуста</div>
          ) : (
            <div>
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <table className="table" style={{ fontSize: '13px' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '110px', textAlign: 'center' }}>Тип</th>
                      <th style={{ width: '160px', textAlign: 'center' }}>Дата</th>
                      <th style={{ width: '110px', textAlign: 'center' }}>Статус</th>
                      <th style={{ width: '100px', textAlign: 'center' }}>Сумма</th>
                      <th style={{ width: '110px', textAlign: 'center' }}>Начислено</th>
                      <th style={{ width: '100px', textAlign: 'center' }}>Баланс</th>
                      <th style={{ textAlign: 'center' }}>Комментарий</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientOrders.map((item, idx) => (
                      <tr key={`${item.type}-${item.id}-${idx}`}
                        className={item.type === 'order' ? 'history-row-order' : ''}
                        style={{ cursor: (item.type === 'order' || item.cert_id) ? 'pointer' : 'default' }}
                        onClick={() => {
                          if (item.type === 'order') openOrderView(item.id);
                          else if (item.cert_id) setSelectedCertView(item.cert_id);
                        }}>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          {item.type === 'order' ? (
                            item.subscription_id ? (
                              <span style={{ color: '#3A83BB', fontWeight: 500 }}>
                                Подписка #{item.subscription_order_number || item.subscription_id}
                                {item.subscription_delivery_number ? `/${item.subscription_delivery_number}` : ''}
                              </span>
                            ) : (
                              <span style={{ color: '#3A83BB', fontWeight: 500 }}>Заказ #{item.order_number || item.id}</span>
                            )
                          ) : item.type === 'cert_purchase' ? (
                            <span style={{ color: '#BD8E00', fontWeight: 500 }}>Сертификат{item.order_number ? ` №${item.order_number}` : ''}</span>
                          ) : item.type === 'cert_activation' ? (
                            <span style={{ color: '#359250', fontWeight: 600 }}>Активация{item.order_number ? ` №${item.order_number}` : ''}</span>
                          ) : (
                            <div>
                              <span style={{ color: item.bonus_earned > 0 ? '#359250' : '#CB4D45', fontWeight: 600 }}>Корректировка</span>
                              {item.employee_name && <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>{item.employee_name}</div>}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '6px 8px', fontSize: '12px', color: '#666', textAlign: 'center' }}>{formatDate(item.date)}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          {item.status ? (
                            <span className={`badge badge-${statusLabels[item.status]?.class || 'os-new'}`}>{statusLabels[item.status]?.label || item.status}</span>
                          ) : <span style={{ color: '#ccc' }}>—</span>}
                        </td>
                        <td style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'center' }}>
                          {item.total_price ? `${item.total_price} ₽` : <span style={{ color: '#ccc' }}>—</span>}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          {item.bonus_earned > 0 ? <span style={{ color: '#359250', fontWeight: 600 }}>+{item.bonus_earned} ₽</span>
                            : item.bonus_spent > 0 ? <span style={{ color: '#CB4D45', fontWeight: 600 }}>-{item.bonus_spent} ₽</span>
                            : <span style={{ color: '#ccc' }}>—</span>}
                        </td>
                        <td style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'center' }}>
                          {item.balance_after !== null && item.balance_after !== undefined
                            ? <span style={{ color: 'var(--primary-color)' }}>{item.balance_after} ₽</span>
                            : <span style={{ color: '#ccc' }}>—</span>}
                        </td>
                        <td style={{ padding: '6px 8px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
                          {item.comment || <span style={{ color: '#ccc' }}>—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {hasMoreOrders && (
                <div style={{ textAlign: 'center', marginTop: '12px' }}>
                  <button className="btn btn-secondary" onClick={handleLoadMoreOrders} disabled={loadingOrders} style={{ fontSize: '13px', padding: '6px 12px' }}>
                    {loadingOrders ? 'Загрузка...' : 'Загрузить еще'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {selectedOrderView && (
        <OrderModal
          initialOrder={selectedOrderView}
          onClose={() => { setSelectedOrderView(null); refreshClientAndHistory(); }}
          showToast={showToast}
        />
      )}

      {selectedCertView && (
        <CertificateDetailModal
          certId={selectedCertView}
          onClose={() => { setSelectedCertView(null); refreshClientAndHistory(); }}
          showToast={showToast}
        />
      )}
    </>
  );
}

export default ClientProfileModal;
